import { useState, useRef, useEffect } from 'react'

import { useSelector, useDispatch } from 'react-redux'

import Text from '../TextMessage/components/Text'
import { getOptionBasedOnLevel } from './utils'
import { changeBotMessageToText, sendMessage, setNewMessage } from 'app/redux/messages/messages.actions'
import BotReturnButton from '../BotReturnButton'
import './index.scss'

const getAllBotsData = ({ widgetData }) => widgetData.widgetInfo.data?.responseBotData
const getLanguageInfo = ({ widgetData }) => widgetData.widgetInfo.languageInfo

// Note that botMessageLevel is based on the human familiar concept of level not index
function RobotOptionsBox({ msgBotLevel, botId, teamId, tmpId }) {
  const elementRef = useRef()
  const [botMessageLevel, setBotMessageLevel] = useState(String(msgBotLevel))

  const dispatch = useDispatch()
  const allBotsData = useSelector(getAllBotsData)
  const languageInfo = useSelector(getLanguageInfo)

  const selectedBot = allBotsData.find((item) => item.id === botId)
  const [question, options] = getOptionBasedOnLevel(selectedBot, botMessageLevel)

  const returnButtonText = languageInfo.texts.botReturnButton

  // dispatching user's selected option's text
  const sendSelectedOptionMessage = (selectedOption) => {
    const messageObject = {
      me: true,
      type: 'text',
      content: selectedOption.question,
      notSendToServer: true,
      createdAt: new Date(),
      botId,
    }

    dispatch(sendMessage({ message: messageObject, teamId }))
  }

  const sendOperatorResponseAfterOptionSelect = (option, newBotMessageLevel, isLastLevelOption) => {
    if (['text', 'event'].includes(option.responseType)) {
      dispatch(
        setNewMessage({
          message: {
            me: false,
            botId: selectedBot.id,
            botMessageLevel,
            content: option.response,
            tmpId: new Date().getTime(),
            isLastLevelOption,
            createdAt: new Date(),
            type: 'bot_text_response',
          },
          teamId,
        }),
      )
    }

    if (option.responseType === 'question') {
      dispatch(
        setNewMessage({
          message: {
            type: 'bot_message',
            botId,
            tmpId: new Date().getTime(),
            botMessageLevel: newBotMessageLevel,
            me: false,
            createdAt: new Date(),
          },
          teamId,
        }),
      )
    }
  }

  const handleClickBotOption = (optionIndex, e) => {
    const selectedOption = options[optionIndex]
    const { responseType } = selectedOption
    const isLastLevelOption = !selectedOption.options
    const newBotMessageLevel = botMessageLevel ? `${botMessageLevel}_${optionIndex + 1}` : optionIndex + 1

    // event responseType
    if (responseType === 'event') {
      const customEvent = new CustomEvent(selectedOption.responseEvent, {
        bubbles: true,
      })

      e.target.dispatchEvent(customEvent)
    }

    // dispatch user's response based on selected option's message
    sendSelectedOptionMessage(selectedOption)

    sendOperatorResponseAfterOptionSelect(selectedOption, newBotMessageLevel, isLastLevelOption)

    dispatch(changeBotMessageToText(tmpId, teamId, question))
  }

  /**
   * renders options according to their response type
   * if responseType is "redirect" then link tag will be rendered
   */
  const renderOptionsBox = options.map((item, index) => {
    const OptionTag = item.responseType === 'redirect' ? 'a' : 'div'
    const { responseType } = item

    const redirectResponseTypeProps = {
      href: item.response,
      target: '_blank',
    }

    const otherResponseTypeProps = {
      onClick: (e) => handleClickBotOption(index, e),
    }

    // assiging props according to tag type
    const props = {
      ...(responseType === 'redirect' ? redirectResponseTypeProps : otherResponseTypeProps),
    }

    return (
      <OptionTag key={index} {...props} className="robot_selectbox__option">
        {item.question}
      </OptionTag>
    )
  })

  const handleClickReturnButton = () => {
    // one step back
    const newBotMessageLevel = botMessageLevel.substring(0, botMessageLevel.lastIndexOf('_'))

    setBotMessageLevel(newBotMessageLevel)
  }

  // return button should not render at the first level
  const shouldRenderReturnButton = botMessageLevel.length !== 0
  // const shouldRenderReturnButton = true;
  const renderReturnButton = shouldRenderReturnButton && (
    <div className="robot_msg_return_btn_wrapper">
      <BotReturnButton handleClick={handleClickReturnButton} text={returnButtonText} />
    </div>
  )

  useEffect(() => {
    elementRef.current?.scrollIntoView()
  }, [])

  return (
    <div className="robot_container" ref={elementRef}>
      {/* header */}
      <Text type="operator" textContent={question} />

      {/* options */}
      <div className="robot_selectbox__options_container">{renderOptionsBox}</div>
      {renderReturnButton}
    </div>
  )
}

export default RobotOptionsBox
